import { useCallback, useEffect, useState } from "react";
import { setActiveCategories, type CategoryDef } from "@torre/expenses";
import { fetchCategories } from "../services/expensesService";

// ---------------------------------------------------------------------------
// Catálogo de categorías de la compañía (#1255). Se carga una vez al abrir la
// app y queda como catálogo ACTIVO de `@torre/expenses`: de ahí salen nombres,
// colores, opciones de los selectores y si un gasto es fijo o variable, en
// todas las pantallas, sin pasarlo de mano en mano.
//
// La pantalla espera a que cargue (`ready`) para no pintar primero con las
// categorías del sistema y luego con las de la compañía. Si falla, se sigue
// con las del sistema: la app no se cae por esto.
// ---------------------------------------------------------------------------

export interface CategoryCatalogState {
  ready: boolean;
  /** Sube en cada carga: sirve de `key` para repintar lo que ya estaba en pantalla. */
  version: number;
  categories: readonly CategoryDef[];
  reload: () => Promise<void>;
}

export function useCategoryCatalog(enabled = true): CategoryCatalogState {
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);
  const [categories, setCategories] = useState<readonly CategoryDef[]>([]);

  const reload = useCallback(async () => {
    try {
      const r = await fetchCategories();
      const list = Array.isArray(r?.categories) ? r.categories : [];
      setActiveCategories(list.length ? list : null);
      setCategories(list);
    } catch {
      setActiveCategories(null);
      setCategories([]);
    } finally {
      setVersion((v) => v + 1);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  // Al salir de la app, el catálogo de esta compañía deja de ser el activo.
  useEffect(() => () => setActiveCategories(null), []);

  return { ready, version, categories, reload };
}
